import _ from 'lodash'
/**
 * This file contains the data types that can be attached to any action triggers. These can be
 * used to supply input to an action from the trigger that caused it to be executed.
 */
import * as yup from 'yup'
import { z } from 'zod'
import {
  DemoAutoTriggerMetadata,
  SupportedA2bFulfillmentWebhookTopics,
  SupportedAirtableWebhookTopics,
  SupportedAmazonSellerWebhookTopics,
  SupportedBillWebhookTopics,
  SupportedCartonCloudWebhookTopics,
  SupportedDossBankingWebhookTopics,
  SupportedDossBillPayWebhookTopics,
  SupportedEasyPostWebhookTopics,
  SupportedExtensivWebhookTopics,
  SupportedFenoWebhookTopics,
  SupportedFirstbaseWebhookTopics,
  SupportedFlexportWebhookTopics,
  SupportedFortySeasWebhookTopics,
  SupportedGmailWebhookTopics,
  SupportedGnosisWebhookTopics,
  SupportedHubSpotWebhookTopics,
  SupportedIngramMicroWebhookTopics,
  SupportedGofreightWebhookTopics,
  SupportedJayGroupWebhookTopics,
  SupportedJazzWebhookTopics,
  SupportedLazadaWebhookTopics,
  SupportedLocadWebhookTopics,
  SupportedLogiwaWebhookTopics,
  SupportedLoopReturnsWebhookTopics,
  SupportedMayaWebhookTopics,
  SupportedMintsoftWebhookTopics,
  SupportedMisfitsMarketWebhookTopics,
  SupportedNavisphereWebhookTopics,
  SupportedOutlookWebhookTopics,
  SupportedQuickbooksWebhookTopics,
  SupportedRilletWebhookTopics,
  SupportedRoutableWebhookTopics,
  SupportedSalsifyWebhookTopics,
  SupportedShipBobWebhookTopics,
  SupportedShipHeroWebhookTopics,
  SupportedShipMonkWebhookTopics,
  SupportedShipWireWebhookTopics,
  SupportedShopeeWebhookTopics,
  SupportedShopifyWebhookTopics,
  SupportedSilqWebhookTopics,
  SupportedStordWebhookTopics,
  SupportedStripeWebhookTopics,
  SupportedThinkLogisticsWebhookTopics,
  SupportedTikTokShopWebhookTopics,
  SupportedUSASupplyPortalWebhookTopics,
  SupportedWalmartWebhookTopics,
  SupportedWooCommerceWebhookTopics,
} from './action-auto-trigger.js'
import { actionDataSchema } from './action-data.js'
import { resolvedFormV2DataSchema } from './form-v2.js'
import { formDataSchema } from './form.js'
import { emailWebhookDataSchema } from './integration-webhook.js'

/**
 * The types of supported action triggers
 */
export enum ActionTriggerType {
  AUTO = 'auto',
  FREQUENCY = 'frequency',
  MANUAL = 'manual',
}

/**
 * The type of data that is supplied with an action trigger
 */
// Generated from ActionAutoTriggerType in types.graphql via @tsEnum codegen
import { ActionTriggerDataType } from '../../generated/ts-enums.generated.js'
export { ActionTriggerDataType }

// Schemas

export const tableCellOutputSchema = yup
  .object({
    id: yup.string().required(),
    columnId: yup.string().required(),
    data: actionDataSchema.nullable().defined(),
    lookupCells: yup.array(
      yup.object({
        id: yup.string().required(),
        rowId: yup.string().required(),
        columnId: yup.string().required(),
      }),
    ),
    documentIds: yup.array(yup.string().defined()),
    /**
     * The documents `documentIds` points at, resolved. Mirrors `users`, which has always been
     * resolved rather than a bare id list. `documentIds` is left alone so existing consumers and
     * mappings keep working.
     */
    documents: yup.array(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        /**
         * Null, never '', for a document with no stored fingerprint. A blank string would compare
         * equal to another blank and read as a match to anything gating on the hash.
         *
         * Also null when the cell came from an Elasticsearch row indexed before these fields
         * existed. Absence is normalised to null at that boundary rather than left undefined.
         */
        contentSha256: yup.string().nullable().defined(),
        contentSizeBytes: yup.number().nullable().defined(),
        documentType: yup.string().nullable().defined(),
      }),
    ),
    users: yup.array(
      yup.object({
        id: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        email: yup.string().nullable().defined(),
        phoneNumber: yup.string().nullable().defined(),
      }),
    ),
    formattedValue: yup.string().nullable().defined(),
  })
  .strict()
export type TableCellOutput = yup.InferType<typeof tableCellOutputSchema>

// Form V2-specific lookup cell type with resolved fields
export const formV2LookupCellOutputSchema = yup.object({
  cellId: yup.string().required(),
  rowId: yup.string().required(),
  columnId: yup.string().required(),
  resolvedCellId: yup.string().nullable(),
  resolvedRowId: yup.string().nullable(),
})
export type FormV2LookupCellOutput = yup.InferType<typeof formV2LookupCellOutputSchema>

// Form V2-specific table cell output type
export const formV2TableCellOutputSchema = yup
  .object({
    cellId: yup.string().required(),
    columnId: yup.string().required(),
    data: actionDataSchema.nullable().defined(),
    tableCells: yup.array(formV2LookupCellOutputSchema),
    documentIds: yup.array(yup.string().defined()),
    users: yup.array(
      yup.object({
        id: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        email: yup.string().nullable().defined(),
        phoneNumber: yup.string().nullable().defined(),
      }),
    ),
    formattedValue: yup.string().nullable().defined(),
  })
  .strict()
export type FormV2TableCellOutput = yup.InferType<typeof formV2TableCellOutputSchema>

// Form V2-specific cells output (record keyed by column ID)
export const formV2CellsOutputSchema = yup.lazy((obj) =>
  yup.object(_.mapValues(obj, () => formV2TableCellOutputSchema.required())).required(),
)
export type FormV2CellsOutput = Record<string, FormV2TableCellOutput>

// Form V2-specific table row output type (with Form V2 cells)
export const formV2TableRowOutputSchema = yup.object({
  id: yup.string().required(),
  createdAt: yup.string().required(),
  parentRowId: yup.string().nullable().defined(),
  cells: formV2CellsOutputSchema,
})
export type FormV2TableRowOutput = yup.InferType<typeof formV2TableRowOutputSchema>

/**
 * Schema for the "cells" output object from table row insert or update steps. The object can
 * contain any string key for the column ID, and the value is an object containing either a cell's
 * data or lookup cell IDs.
 */
export const rowCellsOutputSchema = yup.lazy((obj) =>
  yup.object(_.mapValues(obj, () => tableCellOutputSchema.required())).required(),
)
export type CellsOutput = yup.InferType<typeof rowCellsOutputSchema>

export const tableRowOutputSchema = yup
  .object({
    id: yup.string().required(),
    createdAt: yup.string().required(),
    updatedAt: yup.string().required(),
    createdBy: yup
      .object({
        id: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        email: yup.string().nullable().defined(),
        phoneNumber: yup.string().nullable().defined(),
      })
      .notRequired(),
    updatedBy: yup
      .object({
        id: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        email: yup.string().nullable().defined(),
        phoneNumber: yup.string().nullable().defined(),
      })
      .notRequired(),
    parentRowId: yup.string().nullable(),
    cells: rowCellsOutputSchema,
  })
  .strict()
export type TableRowOutput = yup.InferType<typeof tableRowOutputSchema>

export const manualTriggerDataSchema = yup
  .object({
    triggeredAt: yup.string().required(),
    triggeredBy: yup
      .object({
        userId: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        fullName: yup.string().required(),
        email: yup.string().nullable(),
      })
      .nullable(),
    row: tableRowOutputSchema.notRequired(),
    workspaceButtonId: yup.string().notRequired(),
  })
  .strict()

export const frequencyTriggerDataSchema = yup
  .object({ triggeredAt: yup.string().required() })
  .strict()

export const formActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.FORM] as const)
      .required(),
    triggeredAt: yup.string().required(),
    triggeredBy: yup
      .object({
        userId: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        fullName: yup.string().required(),
        email: yup.string().nullable(),
      })
      .nullable(),
    formData: formDataSchema.required(),
  })
  .strict()

export const formV2ActionTriggerDataSchema = z
  .object({
    type: z.literal(ActionTriggerDataType.FORM_V2),
    triggeredAt: z.string(),
    triggeredBy: z
      .object({
        userId: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        fullName: z.string(),
        email: z.string().nullable(),
      })
      .nullable()
      .optional(),
    formId: z.string(),
    formName: z.string(),
    formData: resolvedFormV2DataSchema,
  })
  .strict()

export const addRowActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.ADD_ROW] as const)
      .required(),
    triggeredAt: yup.string().required(),
    triggeredBy: yup
      .object({
        userId: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        fullName: yup.string().required(),
        email: yup.string().nullable(),
      })
      .nullable(),
    row: tableRowOutputSchema.required(),
  })
  .strict()

export const updateCellActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.UPDATE_CELL] as const)
      .required(),
    triggeredAt: yup.string().required(),
    triggeredBy: yup
      .object({
        userId: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        fullName: yup.string().required(),
        email: yup.string().nullable(),
      })
      .nullable(),
    cellId: yup.string().required(),
    row: tableRowOutputSchema.required(),
    previousValue: tableCellOutputSchema.required(),
    newValue: tableCellOutputSchema.required(),
  })
  .strict()

export const updateRowActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.UPDATE_ROW] as const)
      .required(),
    triggeredAt: yup.string().required(),
    triggeredBy: yup
      .object({
        userId: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        fullName: yup.string().required(),
        email: yup.string().nullable(),
      })
      .nullable(),
    row: tableRowOutputSchema.required(),
    previousValues: rowCellsOutputSchema,
    newValues: rowCellsOutputSchema,
  })
  .strict()

export const deleteRowActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.DELETE_ROW] as const)
      .required(),
    triggeredAt: yup.string().required(),
    triggeredBy: yup
      .object({
        userId: yup.string().required(),
        firstName: yup.string().required(),
        lastName: yup.string().defined(),
        fullName: yup.string().required(),
        email: yup.string().nullable(),
      })
      .nullable(),
    row: tableRowOutputSchema.required(),
  })
  .strict()

export const shopifyWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SHOPIFY_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    shopUrl: yup.string(),
    topic: yup.string().oneOf(Object.values(SupportedShopifyWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const quickbooksWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.QUICKBOOKS_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedQuickbooksWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const gmailWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.GMAIL_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedGmailWebhookTopics)).required(),
    data: emailWebhookDataSchema.nullable(),
  })
  .strict()

export const gnosisWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.GNOSIS_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedGnosisWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const outlookWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.OUTLOOK_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedOutlookWebhookTopics)).required(),
    data: emailWebhookDataSchema.required(),
  })
  .strict()

export const wooCommerceWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.WOO_COMMERCE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedWooCommerceWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const loopReturnsWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.LOOP_RETURNS_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedLoopReturnsWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const logiwaWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.LOGIWA_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedLogiwaWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const thinkLogisticsWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.THINK_LOGISTICS_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedThinkLogisticsWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const locadWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.LOCAD_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedLocadWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const extensivWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.EXTENSIV_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedExtensivWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const easyPostWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.EASYPOST_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedEasyPostWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const flexportWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.FLEXPORT_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedFlexportWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const stripeWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.STRIPE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedStripeWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const billWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.BILL_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedBillWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const ingramMicroWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.INGRAM_MICRO_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedIngramMicroWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const jayGroupWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.JAY_GROUP_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedJayGroupWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const gofreightWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.GOFREIGHT_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedGofreightWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const amazonSellerWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.AMAZON_SELLER_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedAmazonSellerWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()
export const hubSpotWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.HUBSPOT_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedHubSpotWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()
export const shipBobWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SHIPBOB_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedShipBobWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()
export const shipHeroWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SHIPHERO_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedShipHeroWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const shipMonkWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SHIPMONK_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedShipMonkWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const cartonCloudWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.CARTONCLOUD_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedCartonCloudWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const a2bFulfillmentWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.A2B_FULFILLMENT_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedA2bFulfillmentWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const mintsoftWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.MINTSOFT_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedMintsoftWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const firstbaseWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.FIRSTBASE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedFirstbaseWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const fenoWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.FENO_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedFenoWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const dossBankingWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.DOSS_BANKING_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedDossBankingWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const dossBillPayWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.DOSS_BILL_PAY_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedDossBillPayWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const tikTokShopWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.TIKTOK_SHOP_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedTikTokShopWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const usaSupplyPortalWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.USA_SUPPLY_PORTAL_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedUSASupplyPortalWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const mayaWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.MAYA_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedMayaWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const misfitsMarketWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.MISFITS_MARKET_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedMisfitsMarketWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const navisphereWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.NAVISPHERE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedNavisphereWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const airtableWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.AIRTABLE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedAirtableWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const salsifyWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SALSIFY_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedSalsifyWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const shipWireWebhookActionTriggerDataSchema = yup.object({
  type: yup
    .string()
    .oneOf([ActionTriggerDataType.SHIPWIRE_WEBHOOK] as const)
    .required(),
  triggeredAt: yup.string().required(),
  topic: yup.string().oneOf(Object.values(SupportedShipWireWebhookTopics)).required(),
  data: yup.object().json().required(),
})

export const silqWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SILQ_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedSilqWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const stordWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.STORD_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedStordWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const fortySeasWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.FORTY_SEAS_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedFortySeasWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const routableWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.ROUTABLE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedRoutableWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const walmartWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.WALMART_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedWalmartWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const rilletWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.RILLET_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedRilletWebhookTopics)).required(),
    data: yup.mixed().required(),
  })
  .strict()

export const jazzWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.JAZZ_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedJazzWebhookTopics)).required(),
    data: yup.mixed().required(),
  })
  .strict()

export const shopeeWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.SHOPEE_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedShopeeWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

export const lazadaWebhookActionTriggerDataSchema = yup
  .object({
    type: yup
      .string()
      .oneOf([ActionTriggerDataType.LAZADA_WEBHOOK] as const)
      .required(),
    triggeredAt: yup.string().required(),
    topic: yup.string().oneOf(Object.values(SupportedLazadaWebhookTopics)).required(),
    data: yup.object().json().required(),
  })
  .strict()

// Inferred types

/**
 * Metadata that is attached to an action that was triggered manually
 */
export type ManualTriggerData = yup.InferType<typeof manualTriggerDataSchema>

/**
 * Metadata that is attached to an action that was triggered via a frequency trigger
 */
export type FrequencyTriggerData = yup.InferType<typeof frequencyTriggerDataSchema>

/**
 * Form submission data that is attached to an action that was triggered by a form submission
 */
export type FormActionTriggerData = yup.InferType<typeof formActionTriggerDataSchema>

/**
 * Form V2 submission data that is attached to an action that was triggered by a form V2 submission
 */
export type FormV2ActionTriggerData = z.infer<typeof formV2ActionTriggerDataSchema>

/**
 * Trigger Metadata for an action triggered by the addition of a new row to a table
 */
export type AddRowActionTriggerData = yup.InferType<typeof addRowActionTriggerDataSchema>

/**
 * Trigger Metadata for an action triggered by the update of a cell in a table
 */
export type UpdateCellActionTriggerData = yup.InferType<typeof updateCellActionTriggerDataSchema>

/**
 * Trigger Metadata for an action triggered by the update of a row in a table
 */
export type UpdateRowActionTriggerData = yup.InferType<typeof updateRowActionTriggerDataSchema>

/**
 * Trigger Metadata for an action triggered by the deletion of a row from a table
 */
export type DeleteRowActionTriggerData = yup.InferType<typeof deleteRowActionTriggerDataSchema>

/**
 * Shopify event data that is attached to an action triggered by a Shopify webhook
 */
export type ShopifyWebhookActionTriggerData = yup.InferType<
  typeof shopifyWebhookActionTriggerDataSchema
>

/**
 * Quickbooks event data that is attached to an action triggered by a Quickbooks webhook
 */
export type QuickbooksWebhookActionTriggerData = yup.InferType<
  typeof quickbooksWebhookActionTriggerDataSchema
>

/**
 * Gmail event data that is attached to an action triggered by a Gmail webhook
 */
export type GmailWebhookActionTriggerData = yup.InferType<
  typeof gmailWebhookActionTriggerDataSchema
>

/**
 * Gnosis event data that is attached to an action triggered by a Gnosis webhook
 */
export type GnosisWebhookActionTriggerData = yup.InferType<
  typeof gnosisWebhookActionTriggerDataSchema
>

/**
 * Outlook event data that is attached to an action triggered by an Outlook webhook
 */
export type OutlookWebhookActionTriggerData = yup.InferType<
  typeof outlookWebhookActionTriggerDataSchema
>

/**
 * WooCommerce event data that is attached to an action triggered by a WooCommerce webhook
 */
export type WooCommerceWebhookActionTriggerData = yup.InferType<
  typeof wooCommerceWebhookActionTriggerDataSchema
>

/**
 * WooCommerce event data that is attached to an action triggered by a WooCommerce webhook
 */
export type LoopReturnsWebhookActionTriggerData = yup.InferType<
  typeof loopReturnsWebhookActionTriggerDataSchema
>

/**
 * Logiwa event data that is attached to an action triggered by a Logiwa webhook
 */
export type LogiwaWebhookActionTriggerData = yup.InferType<
  typeof logiwaWebhookActionTriggerDataSchema
>

export type ThinkLogisticsWebhookActionTriggerData = yup.InferType<
  typeof thinkLogisticsWebhookActionTriggerDataSchema
>

export type LocadWebhookActionTriggerData = yup.InferType<
  typeof locadWebhookActionTriggerDataSchema
>

/**
 * Extensiv event data that is attached to an action triggered by a Extensiv webhook
 */
export type ExtensivWebhookActionTriggerData = yup.InferType<
  typeof extensivWebhookActionTriggerDataSchema
>

/**
 * Flexport event data that is attached to an action triggered by a Flexport webhook
 */
export type FlexportWebhookActionTriggerData = yup.InferType<
  typeof flexportWebhookActionTriggerDataSchema
>

/**
 * Stripe event data that is attached to an action triggered by a Stripe webhook
 */
export type StripeWebhookActionTriggerData = yup.InferType<
  typeof stripeWebhookActionTriggerDataSchema
>

/**
 * Bill.com event data that is attached to an action triggered by a Bill.com webhook
 */
export type BillWebhookActionTriggerData = yup.InferType<typeof billWebhookActionTriggerDataSchema>

/**
 * IngramMicro event data that is attached to an action triggered by a IngramMicro webhook
 */
export type IngramMicroWebhookActionTriggerData = yup.InferType<
  typeof ingramMicroWebhookActionTriggerDataSchema
>

/**
 * Jay Group event data that is attached to an action triggered by a Jay Group webhook
 */
export type JayGroupWebhookActionTriggerData = yup.InferType<
  typeof jayGroupWebhookActionTriggerDataSchema
>

/**
 * GoFreight event data that is attached to an action triggered by a GoFreight webhook
 */
export type GofreightWebhookActionTriggerData = yup.InferType<
  typeof gofreightWebhookActionTriggerDataSchema
>

/**
 * Amazon Seller event data that is attached to an action triggered by a Amazon Seller webhook
 */
export type AmazonSellerWebhookActionTriggerData = yup.InferType<
  typeof amazonSellerWebhookActionTriggerDataSchema
>

/**
 * HubSpot event data that is attached to an action triggered by a HubSpot webhook
 */
export type HubSpotWebhookActionTriggerData = yup.InferType<
  typeof hubSpotWebhookActionTriggerDataSchema
>

/**
 * ShipBob event data that is attached to an action triggered by a ShipBob webhook
 */
export type SalsifyWebhookActionTriggerData = yup.InferType<
  typeof salsifyWebhookActionTriggerDataSchema
>

export type ShipBobWebhookActionTriggerData = yup.InferType<
  typeof shipBobWebhookActionTriggerDataSchema
>

/**
 * ShipHero event data that is attached to an action triggered by a ShipHero webhook
 */
export type ShipHeroWebhookActionTriggerData = yup.InferType<
  typeof shipHeroWebhookActionTriggerDataSchema
>

/**
 * ShipMonk event data that is attached to an action triggered by a ShipMonk webhook
 */
export type ShipMonkWebhookActionTriggerData = yup.InferType<
  typeof shipMonkWebhookActionTriggerDataSchema
>

/**
 * CartonCloud event data that is attached to an action triggered by a CartonCloud webhook
 */
export type CartonCloudWebhookActionTriggerData = yup.InferType<
  typeof cartonCloudWebhookActionTriggerDataSchema
>

/**
 * Mintsoft event data that is attached to an action triggered by a Mintsoft webhook
 */
export type MintsoftWebhookActionTriggerData = yup.InferType<
  typeof mintsoftWebhookActionTriggerDataSchema
>

export type FirstbaseWebhookActionTriggerData = yup.InferType<
  typeof firstbaseWebhookActionTriggerDataSchema
>

export type FenoWebhookActionTriggerData = yup.InferType<typeof fenoWebhookActionTriggerDataSchema>

/**
 * a2b Fulfillment event data that is attached to an action triggered by an a2b Fulfillment webhook
 */
export type A2bFulfillmentWebhookActionTriggerData = yup.InferType<
  typeof a2bFulfillmentWebhookActionTriggerDataSchema
>

/**
 * Doss Banking event data that is attached to an action triggered by a Doss Banking webhook
 */
export type DossBankingWebhookActionTriggerData = yup.InferType<
  typeof dossBankingWebhookActionTriggerDataSchema
>

/**
 * Doss Bill Pay event data that is attached to an action triggered by a Doss Bill Pay webhook
 */
export type DossBillPayWebhookActionTriggerData = yup.InferType<
  typeof dossBillPayWebhookActionTriggerDataSchema
>

/**
 * TikTok Shop event data that is attached to an action triggered by a Tiktok Shop webhook
 */
export type TikTokShopWebhookActionTriggerData = yup.InferType<
  typeof tikTokShopWebhookActionTriggerDataSchema
>

/**
 * USA Supply Portal event data that is attached to an action triggered by a USA Supply Portal webhook
 */
export type USASupplyPortalWebhookActionTriggerData = yup.InferType<
  typeof usaSupplyPortalWebhookActionTriggerDataSchema
>

/**
 * Maya event data that is attached to an action triggered by a Maya webhook
 */
export type MayaWebhookActionTriggerData = yup.InferType<typeof mayaWebhookActionTriggerDataSchema>

/**
 * Misfits Market event data that is attached to an action triggered by a Misfits Market webhook
 */
export type MisfitsMarketWebhookActionTriggerData = yup.InferType<
  typeof misfitsMarketWebhookActionTriggerDataSchema
>

/**
 * Navisphere event data that is attached to an action triggered by a Navisphere webhook
 */
export type NavisphereWebhookActionTriggerData = yup.InferType<
  typeof navisphereWebhookActionTriggerDataSchema
>

/**
 * Airtable event data that is attached to an action triggered by an Airtable webhook
 */
export type AirtableWebhookActionTriggerData = yup.InferType<
  typeof airtableWebhookActionTriggerDataSchema
>

/**
 * Silq event data that is attached to an action triggered by a Silq webhook
 */
export type SilqWebhookActionTriggerData = yup.InferType<typeof silqWebhookActionTriggerDataSchema>

/**
 * Stord event data that is attached to an action triggered by a Stord webhook
 */
export type StordWebhookActionTriggerData = yup.InferType<
  typeof stordWebhookActionTriggerDataSchema
>

/**
 * 40Seas event data that is attached to an action triggered by a 40Seas webhook
 */
export type FortySeasWebhookActionTriggerData = yup.InferType<
  typeof fortySeasWebhookActionTriggerDataSchema
>

/**
 * Routable event data that is attached to an action triggered by a Routable webhook
 */
export type RoutableWebhookActionTriggerData = yup.InferType<
  typeof routableWebhookActionTriggerDataSchema
>

/**
 * EasyPost event data that is attached to an action triggered by a EasyPost webhook
 */
export type EasyPostWebhookActionTriggerData = yup.InferType<
  typeof easyPostWebhookActionTriggerDataSchema
>

/**
 * Walmart event data that is attached to an action triggered by a Walmart webhook
 */
export type WalmartWebhookActionTriggerData = yup.InferType<
  typeof walmartWebhookActionTriggerDataSchema
>

/**
 * ShipWire event data that is attached to an action triggered by a ShipWire webhook
 */
export type ShipWireWebhookActionTriggerData = yup.InferType<
  typeof shipWireWebhookActionTriggerDataSchema
>

/**
 * Rillet event data that is attached to an action triggered by a Rillet webhook
 */
export type RilletWebhookActionTriggerData = yup.InferType<
  typeof rilletWebhookActionTriggerDataSchema
>

/**
 * Jazz event data that is attached to an action triggered by a Jazz webhook
 */
export type JazzWebhookActionTriggerData = yup.InferType<typeof jazzWebhookActionTriggerDataSchema>

/**
 * Shopee event data that is attached to an action triggered by a Shopee webhook
 */
export type ShopeeWebhookActionTriggerData = yup.InferType<
  typeof shopeeWebhookActionTriggerDataSchema
>

/**
 * Lazada event data that is attached to an action triggered by a Lazada webhook
 */
export type LazadaWebhookActionTriggerData = yup.InferType<
  typeof lazadaWebhookActionTriggerDataSchema
>

export type ActionTriggerData =
  | ManualTriggerData
  | FrequencyTriggerData
  | FormActionTriggerData
  | FormV2ActionTriggerData
  | AddRowActionTriggerData
  | UpdateCellActionTriggerData
  | UpdateRowActionTriggerData
  | DeleteRowActionTriggerData
  | DemoAutoTriggerMetadata
  | A2bFulfillmentWebhookActionTriggerData
  | AirtableWebhookActionTriggerData
  | AmazonSellerWebhookActionTriggerData
  | CartonCloudWebhookActionTriggerData
  | DossBankingWebhookActionTriggerData
  | DossBillPayWebhookActionTriggerData
  | EasyPostWebhookActionTriggerData
  | ExtensivWebhookActionTriggerData
  | FlexportWebhookActionTriggerData
  | FortySeasWebhookActionTriggerData
  | GmailWebhookActionTriggerData
  | GnosisWebhookActionTriggerData
  | HubSpotWebhookActionTriggerData
  | BillWebhookActionTriggerData
  | IngramMicroWebhookActionTriggerData
  | JazzWebhookActionTriggerData
  | JayGroupWebhookActionTriggerData
  | GofreightWebhookActionTriggerData
  | LogiwaWebhookActionTriggerData
  | ThinkLogisticsWebhookActionTriggerData
  | LocadWebhookActionTriggerData
  | MayaWebhookActionTriggerData
  | MisfitsMarketWebhookActionTriggerData
  | MintsoftWebhookActionTriggerData
  | FirstbaseWebhookActionTriggerData
  | FenoWebhookActionTriggerData
  | NavisphereWebhookActionTriggerData
  | OutlookWebhookActionTriggerData
  | QuickbooksWebhookActionTriggerData
  | RilletWebhookActionTriggerData
  | RoutableWebhookActionTriggerData
  | SalsifyWebhookActionTriggerData
  | ShipBobWebhookActionTriggerData
  | ShipHeroWebhookActionTriggerData
  | ShipWireWebhookActionTriggerData
  | ShopeeWebhookActionTriggerData
  | LazadaWebhookActionTriggerData
  | ShopifyWebhookActionTriggerData
  | SilqWebhookActionTriggerData
  | StordWebhookActionTriggerData
  | StripeWebhookActionTriggerData
  | TikTokShopWebhookActionTriggerData
  | USASupplyPortalWebhookActionTriggerData
  | WalmartWebhookActionTriggerData
  | WooCommerceWebhookActionTriggerData

/**
 * Checks if the given metadata is a valid action trigger metadata object
 */
export function isActionTriggerMetadata(
  metadata: Record<string, unknown>,
): metadata is ActionTriggerData {
  if (!('type' in metadata) || typeof metadata.type !== 'string') {
    return manualTriggerDataSchema.isValidSync(metadata)
  }

  const type = metadata.type as ActionTriggerDataType
  if (!Object.values(ActionTriggerDataType).includes(type)) {
    return false
  }

  switch (type) {
    case ActionTriggerDataType.FORM:
      return formActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.FORM_V2:
      return formV2ActionTriggerDataSchema.safeParse(metadata).success
    case ActionTriggerDataType.ADD_ROW:
      return addRowActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.UPDATE_CELL:
      return updateCellActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.UPDATE_ROW:
      return updateRowActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.DELETE_ROW:
      return deleteRowActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SHOPIFY_WEBHOOK:
      return shopifyWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.QUICKBOOKS_WEBHOOK:
      return quickbooksWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.GMAIL_WEBHOOK:
      return gmailWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.GNOSIS_WEBHOOK:
      return gnosisWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.OUTLOOK_WEBHOOK:
      return outlookWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.WOO_COMMERCE_WEBHOOK:
      return wooCommerceWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.LOOP_RETURNS_WEBHOOK:
      return loopReturnsWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.LOGIWA_WEBHOOK:
      return logiwaWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.THINK_LOGISTICS_WEBHOOK:
      return thinkLogisticsWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.LOCAD_WEBHOOK:
      return locadWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.EXTENSIV_WEBHOOK:
      return extensivWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.FLEXPORT_WEBHOOK:
      return flexportWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.FORTY_SEAS_WEBHOOK:
      return fortySeasWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.STRIPE_WEBHOOK:
      return stripeWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.BILL_WEBHOOK:
      return billWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.INGRAM_MICRO_WEBHOOK:
      return ingramMicroWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.JAY_GROUP_WEBHOOK:
      return jayGroupWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.GOFREIGHT_WEBHOOK:
      return gofreightWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.AMAZON_SELLER_WEBHOOK:
      return amazonSellerWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.HUBSPOT_WEBHOOK:
      return hubSpotWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SALSIFY_WEBHOOK:
      return salsifyWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SHIPBOB_WEBHOOK:
      return shipBobWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SHIPHERO_WEBHOOK:
      return shipHeroWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SHIPMONK_WEBHOOK:
      return shipMonkWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.CARTONCLOUD_WEBHOOK:
      return cartonCloudWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.MINTSOFT_WEBHOOK:
      return mintsoftWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.A2B_FULFILLMENT_WEBHOOK:
      return a2bFulfillmentWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.DOSS_BANKING_WEBHOOK:
      return dossBankingWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.DOSS_BILL_PAY_WEBHOOK:
      return dossBillPayWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.EASYPOST_WEBHOOK:
      return easyPostWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.TIKTOK_SHOP_WEBHOOK:
      return tikTokShopWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.USA_SUPPLY_PORTAL_WEBHOOK:
      return usaSupplyPortalWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.MAYA_WEBHOOK:
      return mayaWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.MISFITS_MARKET_WEBHOOK:
      return misfitsMarketWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.NAVISPHERE_WEBHOOK:
      return navisphereWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.AIRTABLE_WEBHOOK:
      return airtableWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SILQ_WEBHOOK:
      return silqWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SHIPWIRE_WEBHOOK:
      return shipWireWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.STORD_WEBHOOK:
      return stordWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.ROUTABLE_WEBHOOK:
      return routableWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.WALMART_WEBHOOK:
      return walmartWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.FIRSTBASE_WEBHOOK:
      return firstbaseWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.RILLET_WEBHOOK:
      return rilletWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.FENO_WEBHOOK:
      return fenoWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.JAZZ_WEBHOOK:
      return jazzWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.SHOPEE_WEBHOOK:
      return shopeeWebhookActionTriggerDataSchema.isValidSync(metadata)
    case ActionTriggerDataType.LAZADA_WEBHOOK:
      return lazadaWebhookActionTriggerDataSchema.isValidSync(metadata)
  }
}

/**
 * Contains the data associated with the trigger caused the action to be executed. The data is keyed
 * by trigger ID.
 */
export type ActionTriggersData =
  | {
      [triggerId: string]: ActionTriggerData
    }
  // Even if an action defines multiple triggers, during runtime there can only be one trigger. This
  // allows us to access the data directly without needing to know the trigger ID.
  | ActionTriggerData
